import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Typography,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
    Container,
    Popover,
    TextField,
    Stack,
    useTheme,
    alpha,
    Snackbar,
    InputAdornment,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    List,
    ListItem,
    Radio,
    Checkbox,
    Avatar,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigation } from '../../contexts/NavigationContext';
import api from '../../services/api';
import { format as formatDate, parseISO as parseISODate } from 'date-fns';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';

const History = () => {
    const theme = useTheme();
    const { navigateTo } = useNavigation();
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [numeroFicha, setNumeroFicha] = useState('');
    const [fichaError, setFichaError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredAnalyses, setFilteredAnalyses] = useState([]);
    const [jiraIssues, setJiraIssues] = useState([]);
    const [selectedJiraIssue, setSelectedJiraIssue] = useState(null);
    const [searching, setSearching] = useState(false);
    const [attachToJira, setAttachToJira] = useState(true);
    const [orderBy, setOrderBy] = useState('created_at');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [attachJiraAnchorEl, setAttachJiraAnchorEl] = useState(null);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [attachJiraIssues, setAttachJiraIssues] = useState([]);
    const [selectedAttachJiraIssue, setSelectedAttachJiraIssue] = useState(null);
    const [attachingToJira, setAttachingToJira] = useState(false);
    const [editFichaModalOpen, setEditFichaModalOpen] = useState(false);
    const [editingAnalysis, setEditingAnalysis] = useState(null);
    const [newFichaNumber, setNewFichaNumber] = useState('');
    const [savingFichaNumber, setSavingFichaNumber] = useState(false);

    const handleSort = (column) => {
        const isAsc = orderBy === column && orderDirection === 'asc';
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    const sortAnalyses = (analyses) => {
        return [...analyses].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Tratamento especial para campos específicos
            if (orderBy === 'user') {
                aValue = a.user?.username || '';
                bValue = b.user?.username || '';
            } else if (orderBy === 'created_at') {
                aValue = new Date(a.created_at).getTime();
                bValue = new Date(b.created_at).getTime();
            } else if (orderBy === 'ficha_number') {
                aValue = parseInt(a.ficha_number) || 0;
                bValue = parseInt(b.ficha_number) || 0;
            } else if (orderBy === 'competent') {
                // Tratamento especial para ordenação do status
                // Usa is_success como fallback quando competent for null
                aValue = a.competent === true ? 2 : 
                         a.competent === false ? 1 : 
                         a.is_success === true ? 2 :
                         a.is_success === false ? 1 : 0;
                bValue = b.competent === true ? 2 : 
                         b.competent === false ? 1 : 
                         b.is_success === true ? 2 :
                         b.is_success === false ? 1 : 0;
            }

            // Comparação
            if (aValue < bValue) {
                return orderDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return orderDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const checkAuthentication = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return false;
        }
        return true;
    };

    const fetchAnalyses = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/analyses');

            if (Array.isArray(response.data)) {
                // Log para debug
                console.log('Análises recebidas:', response.data);
                response.data.forEach(analysis => {
                    console.log(`Análise ${analysis.id}:`, {
                        ficha: analysis.ficha_number,
                        is_success: analysis.is_success,
                        tipo: typeof analysis.is_success
                    });
                });
                setAnalyses(response.data);
            } else {
                throw new Error('Resposta inválida do servidor');
            }
        } catch (error) {
            console.error('Erro:', error);
            const errorMessage = typeof error === 'object' ? 
                error.message || 'Erro ao carregar o histórico' : 
                error;
            setError(errorMessage);
            setAnalyses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        setIsAdmin(userInfo.is_admin || false);
    }, []);

    useEffect(() => {
        if (checkAuthentication()) {
            fetchAnalyses();
        }
    }, []);

    const filterAndSortAnalyses = useCallback(() => {
        if (!analyses.length) {
            setFilteredAnalyses([]);
            return;
        }

        let filtered = [...analyses];
        
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(analysis => {
                const fichaMatch = analysis.ficha_number.toLowerCase().includes(searchLower);
                const userMatch = (analysis.user?.username || '').toLowerCase().includes(searchLower);
                // Verifica o status usando competent ou is_success como fallback
                const statusText = analysis.competent === true ? 'competente' : 
                                  analysis.competent === false ? 'não competente' : 
                                  analysis.is_success === true ? 'competente' :
                                  analysis.is_success === false ? 'não competente' : 
                                  'não definido';
                const statusMatch = statusText.includes(searchLower);
                const actTypeMatch = (analysis.act_type || '').toLowerCase().includes(searchLower);
                
                return fichaMatch || userMatch || statusMatch || actTypeMatch;
            });
        }

        // Aplica a ordenação
        filtered = filtered.sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            if (orderBy === 'user') {
                aValue = a.user?.username || '';
                bValue = b.user?.username || '';
            } else if (orderBy === 'created_at') {
                aValue = new Date(a.created_at).getTime();
                bValue = new Date(b.created_at).getTime();
            } else if (orderBy === 'ficha_number') {
                aValue = parseInt(a.ficha_number) || 0;
                bValue = parseInt(b.ficha_number) || 0;
            }

            if (aValue < bValue) {
                return orderDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return orderDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });

        setFilteredAnalyses(filtered);
    }, [analyses, searchTerm, orderBy, orderDirection]);

    useEffect(() => {
        filterAndSortAnalyses();
    }, [filterAndSortAnalyses]);

    const handleGeneratePDF = async (analysisId, fichaNumber) => {
        try {
            // Verifica se a análise ainda existe na lista atual
            const analysisExists = analyses.some(analysis => analysis.id === analysisId);
            if (!analysisExists) {
                setSnackbarMessage('Análise não encontrada na lista atual. Atualizando...');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
                await fetchAnalyses();
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                setSnackbarMessage('Usuário não autenticado. Por favor, faça login novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            console.log('Gerando PDF para análise:', analysisId);
            setSnackbarMessage('Gerando PDF, por favor aguarde...');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            
            // A URL permanece a mesma pois o backend foi atualizado para usar o novo gerador de PDF
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/pdf/analysis/${analysisId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    await fetchAnalyses();
                    setSnackbarMessage('Análise não encontrada. A lista foi atualizada.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                if (response.status === 403) {
                    setSnackbarMessage('Você não tem permissão para gerar o PDF desta análise.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                const errorText = await response.text();
                throw new Error(errorText);
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error('O PDF gerado está vazio. Por favor, tente novamente.');
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `analise-ficha-${fichaNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setSnackbarMessage('PDF gerado com sucesso no novo formato!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erro detalhado ao gerar PDF:', error);
            setSnackbarMessage(
                error.message === 'Failed to fetch'
                    ? 'Erro de conexão com o servidor. Por favor, verifique sua conexão.'
                    : `Erro ao gerar o PDF: ${error.message}`
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRetry = () => {
        fetchAnalyses();
    };

    const handleNewAnalysis = (event) => {
        setAnchorEl(event.currentTarget);
        setFichaError('');
        setNumeroFicha('');
        setJiraIssues([]);
        setSelectedJiraIssue(null);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const searchJiraIssues = async () => {
        if (!numeroFicha.trim()) {
            setFichaError('Por favor, insira o número da ficha');
            return;
        }
        
        try {
            setSearching(true);
            setFichaError('');
            const response = await api.get(`/settings/jira/search-issue/${numeroFicha}`);
            setJiraIssues(response.data);
            
            if (response.data.length === 0) {
                setFichaError('Nenhuma issue encontrada com este número');
                setSnackbarMessage('Nenhuma issue do JIRA encontrada com este número. Verifique se o número está correto ou tente outro número.');
                setSnackbarSeverity('info');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Erro ao buscar issues do JIRA:', error);
            setFichaError('Erro ao buscar issues do JIRA: ' + (error.response?.data?.detail || error.message));
            setSnackbarMessage('Erro ao buscar issues do JIRA');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setSearching(false);
        }
    };

    const handleStartAnalysis = () => {
        if (!numeroFicha.trim()) {
            setFichaError('Por favor, insira o número da ficha');
            return;
        }

        // Se o checkbox estiver marcado, verifica se uma issue foi selecionada
        if (attachToJira) {
            // Se não buscou nenhuma issue ainda, exige que o usuário busque
            if (jiraIssues.length === 0) {
                setFichaError('É necessário buscar e selecionar uma issue do JIRA para continuar');
                setSnackbarMessage('É necessário buscar e selecionar uma issue do JIRA para continuar');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
                return;
            }
            
            // Se buscou mas não selecionou nenhuma issue
            if (!selectedJiraIssue) {
                setFichaError('É necessário selecionar uma issue do JIRA para continuar');
                setSnackbarMessage('É necessário selecionar uma issue do JIRA para continuar');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
                return;
            }
        }
        
        // Armazena o número da ficha e a issue selecionada no localStorage
        console.log('Salvando número da ficha no localStorage:', numeroFicha.trim());
        localStorage.setItem('numeroFicha', numeroFicha.trim());
        if (selectedJiraIssue) {
            console.log('Salvando issue do JIRA no localStorage:', selectedJiraIssue);
            localStorage.setItem('selectedJiraIssue', JSON.stringify(selectedJiraIssue));
        }
        
        handleClosePopover();
        
        // Usamos navegação direta em vez do context de navegação
        console.log('Navegando para a página raiz para iniciar nova análise');
        navigate('/');
        
        // Adicionamos um log para o usuário
        setSnackbarMessage('Iniciando nova análise...');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    };

    const handleDelete = async (analysisId) => {
        if (!window.confirm('Tem certeza que deseja excluir esta análise?')) {
            return;
        }

        try {
            await api.delete(`/analyses/${analysisId}`);
            setSnackbarMessage('Análise excluída com sucesso!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            fetchAnalyses(); // Recarrega a lista
        } catch (error) {
            console.error('Erro ao excluir análise:', error);
            setSnackbarMessage('Erro ao excluir a análise. Por favor, tente novamente.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const getAvatarUrl = (user) => {
        if (user?.avatar_url) {
            return `${process.env.REACT_APP_API_URL}${user.avatar_url}`;
        }
        return null;
    };

    const handleAttachToJiraClick = async (event, analysis) => {
        try {
            setSelectedAnalysis(analysis);
            setAttachJiraAnchorEl(event.currentTarget);
            setAttachingToJira(true);
            
            const response = await api.get(`/settings/jira/search-issue/${analysis.ficha_number}`);
            setAttachJiraIssues(response.data);
            
            if (response.data.length === 0) {
                setSnackbarMessage('Nenhuma issue encontrada com este número');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Erro ao buscar issues do JIRA:', error);
            setSnackbarMessage('Erro ao buscar issues do JIRA: ' + (error.response?.data?.detail || error.message));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setAttachingToJira(false);
        }
    };

    const handleCloseAttachJiraPopover = () => {
        setAttachJiraAnchorEl(null);
        setSelectedAnalysis(null);
        setAttachJiraIssues([]);
        setSelectedAttachJiraIssue(null);
    };

    const handleAttachPdfToJira = async () => {
        if (!selectedAnalysis || !selectedAttachJiraIssue) return;

        try {
            setAttachingToJira(true);

            // Primeiro gera o PDF
            const token = localStorage.getItem('token');
            const pdfResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/pdf/analysis/${selectedAnalysis.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });

            if (!pdfResponse.ok) {
                if (pdfResponse.status === 404) {
                    await fetchAnalyses();
                    setSnackbarMessage('Análise não encontrada. A lista foi atualizada.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    handleCloseAttachJiraPopover();
                    return;
                }

                if (pdfResponse.status === 403) {
                    setSnackbarMessage('Você não tem permissão para gerar o PDF desta análise.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    handleCloseAttachJiraPopover();
                    return;
                }

                const errorText = await pdfResponse.text();
                throw new Error(`Erro ao gerar PDF: ${errorText}`);
            }

            const pdfBlob = await pdfResponse.blob();
            if (pdfBlob.size === 0) {
                throw new Error('O PDF gerado está vazio. Por favor, tente novamente.');
            }

            const formData = new FormData();
            formData.append('file_path', new File([pdfBlob], `analise-ficha-${selectedAnalysis.ficha_number}.pdf`));
            formData.append('file_name', `analise-ficha-${selectedAnalysis.ficha_number}.pdf`);
            formData.append('analysis_id', selectedAnalysis.id);

            // Anexa o PDF ao JIRA
            await api.post(
                `/settings/jira/issues/${selectedAttachJiraIssue.key}/attachments`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setSnackbarMessage('PDF anexado com sucesso ao JIRA!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            handleCloseAttachJiraPopover();
            fetchAnalyses(); // Atualiza a lista para mostrar o novo status
        } catch (error) {
            console.error('Erro ao anexar PDF ao JIRA:', error);
            setSnackbarMessage(error.message || 'Erro ao anexar PDF ao JIRA. Por favor, tente novamente.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            await fetchAnalyses();
        } finally {
            setAttachingToJira(false);
        }
    };

    // Função para abrir o modal de edição do número da ficha
    const handleEditFichaClick = (analysis) => {
        setEditingAnalysis(analysis);
        setNewFichaNumber(analysis.ficha_number);
        setEditFichaModalOpen(true);
    };

    // Função para fechar o modal de edição
    const handleCloseEditFichaModal = () => {
        setEditFichaModalOpen(false);
        setEditingAnalysis(null);
        setNewFichaNumber('');
    };

    // Função para salvar o novo número da ficha
    const handleSaveFichaNumber = async () => {
        if (!editingAnalysis || !newFichaNumber.trim()) return;

        try {
            setSavingFichaNumber(true);

            // Atualiza o número da ficha na análise
            await api.put(`/analyses/${editingAnalysis.id}`, {
                ficha_number: newFichaNumber.trim()
            });

            setSnackbarMessage('Número da ficha atualizado com sucesso!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Fecha o modal e atualiza a lista
            handleCloseEditFichaModal();
            await fetchAnalyses();
        } catch (error) {
            console.error('Erro ao atualizar número da ficha:', error);
            
            // Verifica se é o erro específico de análise anexada ao JIRA
            if (error.response?.status === 400 && 
                error.response?.data?.detail?.includes('anexada ao JIRA')) {
                setSnackbarMessage('Não é possível alterar o número da ficha de uma análise anexada ao JIRA.');
            } else {
                setSnackbarMessage('Erro ao atualizar o número da ficha. Por favor, tente novamente.');
            }
            
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setSavingFichaNumber(false);
        }
    };

    const columns = [
        {
            id: 'ficha_number',
            label: 'Nº Ficha',
            minWidth: 80,
            maxWidth: 100,
            align: 'left',
        },
        {
            id: 'act_type',
            label: 'Tipo de Ato',
            minWidth: 120,
            maxWidth: 150,
            align: 'left',
        },
        {
            id: 'user',
            label: 'Usuário',
            minWidth: 150,
            maxWidth: 200,
            align: 'left',
        },
        {
            id: 'created_at',
            label: 'Data',
            minWidth: 120,
            maxWidth: 150,
            align: 'left',
            format: (value) => formatDate(parseISODate(value), 'dd/MM/yyyy, HH:mm'),
        },
        {
            id: 'status',
            label: 'Status',
            minWidth: 100,
            maxWidth: 120,
            align: 'left',
        },
        {
            id: 'jira',
            label: 'JIRA',
            minWidth: 100,
            maxWidth: 120,
            align: 'left',
        },
        {
            id: 'actions',
            label: 'Ações',
            minWidth: 80,
            maxWidth: 100,
            align: 'right',
        },
    ];

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: 'calc(100vh - 64px)'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        const errorMessage = typeof error === 'object' ? 
            error.message || 'Erro ao carregar o histórico' : 
            error;

        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: 2,
                minHeight: 'calc(100vh - 64px)',
                p: 4
            }}>
                <Alert severity="error" sx={{ width: '100%', maxWidth: 600 }}>
                    {errorMessage}
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchAnalyses}
                >
                    Tentar Novamente
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#f8fafc',
            py: 4
        }}>
            <Container maxWidth="xl">
                <Box sx={{ width: '100%', mb: 4 }}>
                    <Paper sx={{ 
                        width: '100%', 
                        mb: 2,
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.8)
                            : theme.palette.background.paper,
                        backdropFilter: 'blur(20px)',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)'
                    }}>
                <Box sx={{ 
                            p: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                            borderBottom: '1px solid',
                            borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)',
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Typography variant="h6" component="div" sx={{ flex: '0 0 auto' }}>
                        Histórico de Análises
                    </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 2,
                                flex: { xs: '1 1 100%', md: '0 1 auto' },
                                order: { xs: 3, md: 2 }
                            }}>
                                <TextField
                                    placeholder="Buscar por nº ficha, usuário ou status..."
                                    size="small"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{
                                        minWidth: { xs: '100%', md: '300px' },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.02)',
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSearchTerm('')}
                                                    edge="end"
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewAnalysis}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        minWidth: 'auto'
                        }}
                    >
                        Nova Análise
                    </Button>
                </Box>
                        </Box>
                        <TableContainer 
                            component={Paper} 
                            sx={{ 
                                height: {
                                    xs: 'auto',
                                    md: 'calc(100vh - 250px)'
                                },
                                overflow: {
                                    xs: 'visible',
                                    md: 'auto'
                                },
                                '& .MuiTableCell-root': {
                                    px: {
                                        xs: 1,
                                        sm: 2
                                    },
                                    py: {
                                        xs: 1,
                                        sm: 1.5
                                    },
                                    whiteSpace: {
                                        xs: 'normal',
                                        sm: 'nowrap'
                                    },
                                    fontSize: {
                                        xs: '0.8rem',
                                        sm: 'inherit'
                                    }
                                }
                            }}
                        >
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell 
                                            onClick={() => handleSort('ficha_number')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Nº Ficha
                                                {orderBy === 'ficha_number' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            onClick={() => handleSort('act_type')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Tipo de Ato
                                                {orderBy === 'act_type' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            onClick={() => handleSort('user')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Usuário
                                                {orderBy === 'user' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            onClick={() => handleSort('created_at')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Data
                                                {orderBy === 'created_at' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            onClick={() => handleSort('competent')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                Status
                                                {orderBy === 'competent' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell 
                                            onClick={() => handleSort('jira_issue_key')} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                JIRA
                                                {orderBy === 'jira_issue_key' && (
                                                    orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(filteredAnalyses || [])
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((analysis) => (
                                            <TableRow hover key={analysis.id}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography>{analysis.ficha_number}</Typography>
                                                        {!analysis.jira_attached && !analysis.jira_issue_key && (
                                                            <Tooltip title="Editar número da ficha">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleEditFichaClick(analysis)}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{analysis.act_type || 'Não especificado'}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 1,
                                                        flexWrap: {
                                                            xs: 'wrap',  // Em telas pequenas, permite quebra
                                                            sm: 'nowrap' // Em telas maiores, mantém em uma linha
                                                        }
                                                    }}>
                                                        <Avatar
                                                            src={getAvatarUrl(analysis.user)}
                                                            sx={{ 
                                                                width: { xs: 20, sm: 24 }, 
                                                                height: { xs: 20, sm: 24 }
                                                            }}
                                                        >
                                                            {analysis.user?.username?.[0]?.toUpperCase()}
                                                        </Avatar>
                                                        <Typography sx={{
                                                            fontSize: {
                                                                xs: '0.8rem',
                                                                sm: 'inherit'
                                                            }
                                                        }}>
                                                            {analysis.user?.username}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(new Date(analysis.created_at), 'dd/MM/yyyy HH:mm')}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={analysis.competent === true ? "Competente" : 
                                                               analysis.competent === false ? "Não Competente" : 
                                                               analysis.is_success === true ? "Competente" :
                                                               analysis.is_success === false ? "Não Competente" :
                                                               "Não Definido"}
                                                        color={analysis.competent === true ? "success" : 
                                                               analysis.competent === false ? "error" : 
                                                               analysis.is_success === true ? "success" :
                                                               analysis.is_success === false ? "error" :
                                                               "default"}
                                                        size="small"
                                                        icon={analysis.competent === true ? <CheckCircleIcon fontSize="small" /> :
                                                              analysis.competent === false ? <CancelIcon fontSize="small" /> :
                                                              analysis.is_success === true ? <CheckCircleIcon fontSize="small" /> :
                                                              analysis.is_success === false ? <CancelIcon fontSize="small" /> :
                                                              null}
                                                        sx={{
                                                            minWidth: 120,
                                                            '& .MuiChip-label': {
                                                                px: 1
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {analysis.jira_issue_key ? (
                                                        <Chip
                                                            label={analysis.jira_issue_key}
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Não anexado
                                                            </Typography>
                                                            <Tooltip title="Anexar ao JIRA">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleAttachToJiraClick(e, analysis)}
                                                                    disabled={attachingToJira}
                                                                >
                                                                    <FileCopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <IconButton
                                                            onClick={() => handleDelete(analysis.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleGeneratePDF(analysis.id, analysis.ficha_number)}
                                                        >
                                                            <FileCopyIcon />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredAnalyses.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Itens por página"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                </Paper>
                </Box>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setSnackbarOpen(false)} 
                        severity={snackbarSeverity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        p: 3,
                        maxWidth: 500,
                        width: '100%',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? '#1E1E1E'
                            : theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        mt: 1,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 28,
                            width: 10,
                            height: 10,
                            bgcolor: theme.palette.mode === 'dark'
                                ? '#1E1E1E'
                                : theme.palette.background.paper,
                            transform: 'translateY(-50%) rotate(45deg)',
                            borderLeft: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            borderTop: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 1,
                            right: 24,
                            width: 18,
                            height: 18,
                            bgcolor: theme.palette.mode === 'dark'
                                ? '#1E1E1E'
                                : theme.palette.background.paper,
                            zIndex: 1,
                        }
                    }
                }}
            >
                <Stack spacing={2} sx={{ position: 'relative', zIndex: 2 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark' ? '#fff' : '#1a1f2c'
                        }}
                    >
                        Nova Análise
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Número da Ficha"
                            value={numeroFicha}
                            onChange={(e) => {
                                setNumeroFicha(e.target.value);
                                setFichaError('');
                            }}
                            error={!!fichaError}
                            helperText={fichaError}
                            autoFocus
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(0, 0, 0, 0.2)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(0, 0, 0, 0.3)'
                                            : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    ...(attachToJira && {
                                        '& fieldset': {
                                            borderColor: theme.palette.primary.main,
                                            borderWidth: '2px'
                                        }
                                    })
                                }
                            }}
                        />
                        {attachToJira && (
                            <Button
                                variant="contained"
                                onClick={searchJiraIssues}
                                disabled={searching || !numeroFicha.trim()}
                                startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{
                                    minWidth: '120px',
                                    borderRadius: 1,
                                    textTransform: 'none'
                                }}
                            >
                                {searching ? 'Buscando...' : 'Buscar'}
                            </Button>
                        )}
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={attachToJira}
                                onChange={(e) => {
                                    setAttachToJira(e.target.checked);
                                    if (!e.target.checked) {
                                        setJiraIssues([]);
                                        setSelectedJiraIssue(null);
                                        setFichaError('');
                                    }
                                }}
                            />
                        }
                        label="Desejo anexar a análise no JIRA"
                        sx={{ mt: 2 }}
                    />
                    
                    {attachToJira && (
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                mt: -1, 
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <InfoIcon fontSize="small" color="info" />
                            Digite o número da ficha e clique em "Buscar" para encontrar e selecionar um ato do JIRA
                        </Typography>
                    )}

                    {attachToJira && jiraIssues.length > 0 && (
                        <>
                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                Selecione a Issue:
                            </Typography>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    maxHeight: 200, 
                                    overflow: 'auto',
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(0, 0, 0, 0.2)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <RadioGroup
                                    value={selectedJiraIssue ? selectedJiraIssue.id : ''}
                                    onChange={(e, value) => setSelectedJiraIssue(jiraIssues.find(issue => issue.id === value))}
                                >
                                    <List>
                                        {jiraIssues.map((issue) => (
                                            <ListItem key={issue.id} divider>
                                                <FormControlLabel
                                                    value={issue.id}
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle1">
                                                                {issue.key} - {issue.fields.summary}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Status: {issue.fields.status.name}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </RadioGroup>
                            </Paper>
                        </>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleStartAnalysis}
                        fullWidth
                        disabled={
                            !numeroFicha.trim() || 
                            (attachToJira && (
                                jiraIssues.length === 0 || 
                                !selectedJiraIssue
                            ))
                        }
                        sx={{
                            mt: 3,
                            py: 1.5,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: '1rem',
                            background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(45deg, #90CAF9 30%, #64B5F6 90%)'
                                : 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                            '&:hover': {
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(45deg, #64B5F6 30%, #42A5F5 90%)'
                                    : 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)'
                            }
                        }}
                    >
                        Continuar
                    </Button>
                </Stack>
            </Popover>

            <Popover
                open={Boolean(attachJiraAnchorEl)}
                anchorEl={attachJiraAnchorEl}
                onClose={handleCloseAttachJiraPopover}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        p: 3,
                        maxWidth: 500,
                        width: '100%',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? '#1E1E1E'
                            : theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)',
                        borderRadius: 2
                    }
                }}
            >
                <Stack spacing={2}>
                    <Typography variant="h6" gutterBottom>
                        Anexar PDF ao JIRA
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                        Ficha: {selectedAnalysis?.ficha_number}
                    </Typography>

                    {attachJiraIssues.length > 0 ? (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Selecione a Issue:
                            </Typography>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    maxHeight: 200, 
                                    overflow: 'auto',
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(0, 0, 0, 0.2)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <RadioGroup
                                    value={selectedAttachJiraIssue ? selectedAttachJiraIssue.id : ''}
                                    onChange={(e, value) => setSelectedAttachJiraIssue(attachJiraIssues.find(issue => issue.id === value))}
                                >
                                    <List>
                                        {attachJiraIssues.map((issue) => (
                                            <ListItem key={issue.id} divider>
                                                <FormControlLabel
                                                    value={issue.id}
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle1">
                                                                {issue.key} - {issue.fields.summary}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Status: {issue.fields.status.name}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </RadioGroup>
                            </Paper>

                            <Button
                                variant="contained"
                                onClick={handleAttachPdfToJira}
                                disabled={!selectedAttachJiraIssue || attachingToJira}
                                sx={{
                                    mt: 2,
                                    borderRadius: 1,
                                    textTransform: 'none'
                                }}
                            >
                                {attachingToJira ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Anexando...
                                    </>
                                ) : (
                                    'Anexar PDF'
                                )}
                            </Button>
                        </>
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            Buscando issues...
                        </Typography>
                    )}
                </Stack>
            </Popover>
            
            {/* Modal de edição do número da ficha */}
            <Dialog
                open={editFichaModalOpen}
                onClose={handleCloseEditFichaModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Editar Número da Ficha
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Número da Ficha"
                            value={newFichaNumber}
                            onChange={(e) => setNewFichaNumber(e.target.value)}
                            autoFocus
                            disabled={savingFichaNumber}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(0, 0, 0, 0.2)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                }
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Atenção: Alterar o número da ficha pode dificultar a localização desta análise posteriormente.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseEditFichaModal} 
                        disabled={savingFichaNumber}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveFichaNumber}
                        disabled={!newFichaNumber.trim() || savingFichaNumber}
                        startIcon={savingFichaNumber ? <CircularProgress size={20} /> : null}
                    >
                        {savingFichaNumber ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default History; 